import { useState } from 'react';
import {
  CurrencyFormat,
  Icon,
  Modal,
  NoFound,
  Search,
  Select,
  Spinner,
  Table,
  TableRow,
} from '@/components/generic';
import { cn } from '@/lib/utils';
import { SelectOption } from '@/types/select';
import { copyToClipboard, getCurrencySymbol, shortenAddress } from '@/utils';
import {
  useGetUser3DsTxs,
  useGetUserSpecific3DsTx,
} from '@package/smart-wallet/hooks';
import { useTranslation } from 'react-i18next';

import { useCardContext } from '../context/useCardContext';

// Transaction types with color classifications for 3DS
const THREEDS_TRANSACTION_TYPES = {

  THIRD_3DS_OTP: { label: '3DS OTP', color: 'label-blue', priority: 'medium' },
  AUTH_URL: { label: 'Auth URL', color: 'label-green', priority: 'high' },
  ACTIVATION_CODE: { label: 'Activation Code', color: 'label-yellow', priority: 'medium' },
} as const;

// Type filter options based on hook parameters
const typeFilterOptions: SelectOption[] = [
  { id: 0, name: 'All Types', value: '' },
  { id: 1, name: '3DS OTP', value: 'THIRD_3DS_OTP' },
  { id: 2, name: 'Auth URL', value: 'AUTH_URL' },
  { id: 3, name: 'Activation Code', value: 'ACTIVATION_CODE' },
];

const tableHead = [
  'Card No.',
  'Trade No.',
  'Origin Trade No.',
  'Currency',
  'Amount',
  'Merchant Name',
  'Values',
  'Type',
  'Transaction Time',
  'Description',
  'Action',
];

// Helper function to format date
const formatDate = (dateString: string) => {
  // Handle Unix timestamps (numbers as strings) vs regular date strings
  const timestamp = !isNaN(Number(dateString))
    ? Number(dateString)
    : dateString;
  const date = new Date(timestamp);

  // Check if date is valid
  if (isNaN(date.getTime())) {
    return 'Invalid Date';
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const txDetailsTxType = ['THIRD_3DS_OTP', 'ACTIVATION_CODE'];

const ThreeDSTransactions = () => {
  const { t } = useTranslation();
  const { selectedCard } = useCardContext();
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [selectedTypeFilter, setSelectedTypeFilter] = useState<SelectOption>(
    typeFilterOptions[0],
  );
  const [orderNoFilter, setOrderNoFilter] = useState('');
  const [cardNoFilter, setCardNoFilter] = useState('');
  const [showDetails, setShowDetails] = useState(false);
  const [selectedTradeNo, setSelectedTradeNo] = useState<string | null>(null);

  const {
    isLoading,
    records: transactionsData,
    total,
  } = useGetUser3DsTxs({
    cardId: selectedCard?.id || 0,
    pageNum: currentPage,
    pageSize: pageSize,
    type: selectedTypeFilter.value
      ? (selectedTypeFilter.value as 'THIRD_3DS_OTP' | 'AUTH_URL')
      : undefined,
    orderNo: orderNoFilter || undefined,
    cardNo: cardNoFilter || undefined,
  });

  console.log('>>> Transactions Data:', transactionsData, isLoading) // Debug log for transactions data

  // Hook for getting specific transaction details
  const {
    record: transactionDetails,
    isLoading: isLoadingDetails,
    isError: isDetailsError,
  } = useGetUserSpecific3DsTx({
    cardId: selectedTradeNo ? selectedCard?.id || 0 : 0,
    tradeNo: selectedTradeNo || '',
  });

  // Don't render if no card is selected
  if (!selectedCard?.id) {
    return (
      <div className='card'>
        <div className='flex items-center max-md:h-12'>
          <div className='pl-5 text-h6 max-lg:pl-3 max-md:mr-auto'>
            {t('cards.3ds_transactions')}
          </div>
        </div>
        <NoFound
          title={t('cards.no_card_selected')}
          sub={t('cards.select_card_3ds')}
        />
      </div>
    );
  }

  // Filter transactions based on search and sort
  const filteredTransactions = (transactionsData || []).filter(
    (transaction: any) => {
      const matchesSearch =
        search === '' ||
        String(transaction?.id || transaction?.threedsId || '')
          .toLowerCase()
          .includes(search.toLowerCase()) ||
        String(transaction?.status || '')
          .toLowerCase()
          .includes(search.toLowerCase()) ||
        String(transaction?.note || transaction?.description || '')
          .toLowerCase()
          .includes(search.toLowerCase()) ||
        String(transaction?.cardNo || '')
          .toLowerCase()
          .includes(search.toLowerCase()) ||
        String(transaction?.tradeNo || '')
          .toLowerCase()
          .includes(search.toLowerCase()) ||
        String(transaction?.merchantName || '')
          .toLowerCase()
          .includes(search.toLowerCase()) ||
        THREEDS_TRANSACTION_TYPES[
          transaction?.type as keyof typeof THREEDS_TRANSACTION_TYPES
        ]?.label
          .toLowerCase()
          .includes(search.toLowerCase());

      return matchesSearch;
    },
  );

  // Calculate pagination info
  const totalPages = Math.ceil((total || 0) / pageSize);
  const hasNextPage = currentPage < totalPages;
  const hasPrevPage = currentPage > 1;

  // Handle pagination
  const handleNextPage = () => {
    if (hasNextPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (hasPrevPage) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Reset all filters
  const handleResetFilters = () => {
    setSelectedTypeFilter(typeFilterOptions[0]);
    setOrderNoFilter('');
    setCardNoFilter('');
    setSearch('');
    setCurrentPage(1);
  };

  // Handle view details for 3DS OTP transactions
  const handleViewDetails = (transaction: any) => {
    console.log(transaction.tradeNo);
    if (txDetailsTxType.includes(transaction.type)) {
      setSelectedTradeNo(transaction.tradeNo);
      setShowDetails(true);
    }
  };

  return (
    <>
      <div>
        <div className='flex items-center px-5 py-3 max-lg:px-3'>
          <Search
            className='w-70 mr-auto max-lg:w-64 max-md:w-full'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder='Search...'
            isGray
          />
        </div>
        {/* Additional Filters Section */}
        <div className='flex flex-wrap items-center gap-4 px-5 py-3 max-lg:px-3 border-b border-s-stroke2'>
          <div className='flex items-center gap-2'>
            <label className='text-sm font-medium text-t-secondary'>
              Type:
            </label>
            <Select
              className='w-48'
              value={selectedTypeFilter}
              onChange={setSelectedTypeFilter}
              options={typeFilterOptions}
              placeholder='Select type'
            />
          </div>
          <div className='flex items-center gap-2'>
            <label className='text-sm font-medium text-t-secondary'>
              Order No:
            </label>
            <input
              type='text'
              value={orderNoFilter}
              onChange={(e) => setOrderNoFilter(e.target.value)}
              placeholder='Enter order number'
              className='w-48 h-10 px-3 border border-s-stroke2 rounded-lg text-sm bg-b-surface2 text-t-primary placeholder-t-secondary focus:outline-none focus:border-s-highlight transition-colors'
            />
          </div>
          <div className='flex items-center gap-2'>
            <label className='text-sm font-medium text-t-secondary'>
              Card No:
            </label>
            <input
              type='text'
              value={cardNoFilter}
              onChange={(e) => setCardNoFilter(e.target.value)}
              placeholder='Enter card number'
              className='w-48 h-10 px-3 border border-s-stroke2 rounded-lg text-sm bg-b-surface2 text-t-primary placeholder-t-secondary focus:outline-none focus:border-s-highlight transition-colors'
            />
          </div>
          <button
            onClick={handleResetFilters}
            className='px-4 py-2 text-sm font-medium text-t-secondary border border-s-stroke2 rounded-lg hover:bg-b-surface2 transition-colors'
          >
            Reset Filters
          </button>
        </div>
        <div className='px-5 pb-3 max-lg:px-3'>
          <p className='text-sm text-t-secondary'>
            3D Secure authentication transactions including OTP verifications
            and authorization URLs for enhanced payment security.
          </p>
        </div>

        {/* Fixed height container for content and pagination */}
        <div className='h-[600px] flex flex-col'>
          {isLoading ? (
            <div className='flex-1 flex justify-center items-center'>
              <Spinner />
            </div>
          ) : filteredTransactions.length === 0 ? (
            <div className='flex-1 flex items-center justify-center'>
              <NoFound title='No 3DS transactions found' />
            </div>
          ) : (
            <>
              {/* Scrollable table area */}
              <div className='flex-1 overflow-y-auto p-1 pt-6 max-md:pt-3 max-lg:px-0'>
                <div className='overflow-x-auto w-full'>
                  <div className='min-w-[1200px]'>
                    <Table
                      cellsThead={tableHead.map((head) => (
                        <th
                          className={cn(
                            '!h-12.5 last:text-left whitespace-nowrap',
                          )}
                          key={head}
                        >
                          {head}
                        </th>
                      ))}
                      isMobileVisibleTHead
                    >
                      {filteredTransactions.map((transaction: any, index) => (
                        <TableRow
                          key={
                            transaction?.id || transaction?.threedsId || index
                          }
                        >
                          {/* Card No. */}
                          <td className='font-mono text-sm whitespace-nowrap'>
                            <div className='flex items-center gap-2'>
                              <span>
                                {shortenAddress(
                                  String(transaction?.cardNo || ''),
                                )}
                              </span>
                              <button
                                onClick={() =>
                                  copyToClipboard(
                                    String(transaction?.cardNo || ''),
                                  )
                                }
                                className='p-1 hover:bg-gray-100 rounded transition-colors'
                                title='Copy Card Number'
                              >
                                <Icon
                                  name='copy'
                                  className='!size-4 fill-t-secondary hover:fill-t-primary'
                                />
                              </button>
                            </div>
                          </td>
                          {/* Trade No. */}
                          <td className='font-mono text-sm whitespace-nowrap'>
                            <div className='flex items-center gap-2'>
                              <span>
                                {shortenAddress(
                                  String(transaction?.tradeNo || ''),
                                )}
                              </span>
                              <button
                                onClick={() =>
                                  copyToClipboard(
                                    String(transaction?.tradeNo || ''),
                                  )
                                }
                                className='p-1 hover:bg-gray-100 rounded transition-colors'
                                title='Copy Trade Number'
                              >
                                <Icon
                                  name='copy'
                                  className='!size-4 fill-t-secondary hover:fill-t-primary'
                                />
                              </button>
                            </div>
                          </td>
                          {/* Origin Trade No. */}
                          <td className='font-mono text-sm whitespace-nowrap'>
                            <div className='flex items-center gap-2'>
                              <span>
                                {transaction?.originTradeNo
                                  ? shortenAddress(
                                      String(transaction?.originTradeNo),
                                    )
                                  : 'No Data'}
                              </span>
                              {transaction?.originTradeNo && (
                                <button
                                  onClick={() =>
                                    copyToClipboard(
                                      String(transaction?.originTradeNo),
                                    )
                                  }
                                  className='p-1 hover:bg-gray-100 rounded transition-colors'
                                  title='Copy Origin Trade Number'
                                >
                                  <Icon
                                    name='copy'
                                    className='!size-4 fill-t-secondary hover:fill-t-primary'
                                  />
                                </button>
                              )}
                            </div>
                          </td>
                          {/* Currency */}
                          <td className='text-sm whitespace-nowrap font-medium'>
                            {transaction?.currency || 'No Data'}
                          </td>
                          {/* Amount */}
                          <td className='text-right text-sub-title-2 max-md:text-body-2 max-md:font-normal whitespace-nowrap'>
                            <CurrencyFormat
                              value={Number(transaction?.amount || 0)}
                              currency={getCurrencySymbol(
                                'en-US',
                                transaction?.currency || 'USD',
                              )}
                            />
                          </td>
                          {/* Merchant Name */}
                          <td className='text-sm whitespace-nowrap'>
                            {transaction?.merchantName || 'No Data'}
                          </td>
                          {/* Values */}
                          <td className='font-mono text-sm whitespace-nowrap max-w-32'>
                            <div className='flex items-center gap-2'>
                              <span className='truncate'>
                                {txDetailsTxType.includes(transaction?.type || '') ? (
                                  '****'
                                ) : (
                                  shortenAddress(String(transaction?.values || ''), 8)
                                )}
                              </span>
                              {!txDetailsTxType.includes(transaction?.type || '') && transaction?.values && (
                                <button
                                  onClick={() =>
                                    copyToClipboard(String(transaction?.values))
                                  }
                                  className='p-1 hover:bg-gray-100 rounded transition-colors flex-shrink-0'
                                  title='Copy Values'
                                >
                                  <Icon
                                    name='copy'
                                    className='!size-4 fill-t-secondary hover:fill-t-primary'
                                  />
                                </button>
                              )}
                            </div>
                          </td>
                          {/* Type */}
                          <td>
                            <div className='inline-flex items-center gap-1'>
                              <div
                                className={`label whitespace-nowrap ${
                                  THREEDS_TRANSACTION_TYPES[
                                    transaction?.type as keyof typeof THREEDS_TRANSACTION_TYPES
                                  ]?.color || 'label-gray'
                                }`}
                              >
                                {THREEDS_TRANSACTION_TYPES[
                                  transaction?.type as keyof typeof THREEDS_TRANSACTION_TYPES
                                ]?.label || transaction?.type}
                              </div>
                            </div>
                          </td>
                          {/* Transaction Time */}
                          <td className='max-md:text-caption w-32 whitespace-nowrap'>
                            {formatDate(
                              transaction?.transactionTime ||
                                transaction?.createdAt ||
                                transaction?.created_at ||
                                '',
                            )}
                          </td>
                          {/* Description */}
                          <td className='text-sm whitespace-nowrap'>
                            {transaction?.description ||
                              transaction?.note ||
                              transaction?.remark ||
                              'No Data'}
                          </td>
                          {/* Action */}
                          <td className='text-center whitespace-nowrap'>
                            {txDetailsTxType.includes(transaction?.type || '') ? (
                              <button
                                onClick={() => handleViewDetails(transaction)}
                                className='px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors'
                              >
                                View Details
                              </button>
                            ) : (
                              <span className='text-t-secondary text-sm'>
                                N/A
                              </span>
                            )}
                          </td>
                        </TableRow>
                      ))}
                    </Table>
                  </div>
                </div>
              </div>

              {/* Fixed pagination at bottom */}
              <div className='shrink-0 border-t border-s-stroke2 pt-4 pb-2'>
                <div className='flex items-center justify-between px-6 max-lg:flex-col max-lg:gap-4'>
                  <div className='text-sm text-t-secondary'>
                    Showing page {currentPage} of {totalPages} ({total} total
                    records)
                  </div>
                  <div className='flex items-center gap-2'>
                    <button
                      onClick={handlePrevPage}
                      disabled={!hasPrevPage}
                      className='flex items-center justify-center w-10 h-10 rounded-lg border border-s-stroke2 text-sm font-medium transition-colors hover:bg-b-surface2 disabled:opacity-50 disabled:cursor-not-allowed'
                    >
                      <Icon
                        name='arrow'
                        className='rotate-180 fill-t-secondary'
                      />
                    </button>

                    {/* Page numbers */}
                    <div className='flex items-center gap-1'>
                      {Array.from(
                        { length: Math.min(5, totalPages) },
                        (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }

                          return (
                            <button
                              key={pageNum}
                              onClick={() => handlePageChange(pageNum)}
                              className={`flex items-center justify-center w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                                currentPage === pageNum
                                  ? 'bg-b-primary text-t-primary border border-s-focus'
                                  : 'border border-s-stroke2 hover:bg-b-surface2'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        },
                      )}
                    </div>

                    <button
                      onClick={handleNextPage}
                      disabled={!hasNextPage}
                      className='flex items-center justify-center w-10 h-10 rounded-lg border border-s-stroke2 text-sm font-medium transition-colors hover:bg-b-surface2 disabled:opacity-50 disabled:cursor-not-allowed'
                    >
                      <Icon name='arrow' className='fill-t-secondary' />
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Transaction Details Modal */}
      <Modal
        open={showDetails}
        onClose={() => {
          setShowDetails(false);
          setSelectedTradeNo(null);
        }}
      >
        <div className='p-6'>
          <h2 className='text-xl font-semibold text-t-primary mb-4'>
            3DS OTP Transaction Details
          </h2>
          {isLoadingDetails ? (
            <div className='flex items-center justify-center py-8'>
              <Spinner />
              <span className='ml-2'>Loading transaction details...</span>
            </div>
          ) : isDetailsError ? (
            <div className='text-center py-8'>
              <p className='text-red-500'>
                Error loading transaction details. Please try again.
              </p>
            </div>
          ) : transactionDetails ? (
            <div className='space-y-4'>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4 text-t-primary'>
                <div>
                  <label className='block text-sm font-medium text-t-secondary mb-1'>
                    Card Number
                  </label>
                  <p className='text-sm font-mono bg-b-surface2 p-2 rounded'>
                    {transactionDetails.cardNo || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className='block text-sm font-medium text-t-secondary mb-1'>
                    Trade Number
                  </label>
                  <p className='text-sm font-mono bg-b-surface2 p-2 rounded'>
                    {transactionDetails.tradeNo || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className='block text-sm font-medium text-t-secondary mb-1'>
                    Origin Trade Number
                  </label>
                  <p className='text-sm font-mono bg-b-surface2 p-2 rounded'>
                    {transactionDetails.originTradeNo || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className='block text-sm font-medium text-t-secondary mb-1'>
                    Currency
                  </label>
                  <p className='text-sm bg-b-surface2 p-2 rounded'>
                    {transactionDetails.currency || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className='block text-sm font-medium text-t-secondary mb-1'>
                    Amount
                  </label>
                  <p className='text-sm bg-b-surface2 p-2 rounded'>
                    <CurrencyFormat
                      value={Number(transactionDetails.amount || 0)}
                      currency={getCurrencySymbol(
                        'en-US',
                        transactionDetails.currency || 'USD',
                      )}
                    />
                  </p>
                </div>
                <div>
                  <label className='block text-sm font-medium text-t-secondary mb-1'>
                    Merchant Name
                  </label>
                  <p className='text-sm bg-b-surface2 p-2 rounded'>
                    {transactionDetails.merchantName || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className='block text-sm font-medium text-t-secondary mb-1'>
                    Type
                  </label>
                  <p className='text-sm bg-b-surface2 p-2 rounded'>
                    <span
                      className={`label ${THREEDS_TRANSACTION_TYPES[transactionDetails.type as keyof typeof THREEDS_TRANSACTION_TYPES]?.color || 'label-gray'}`}
                    >
                      {THREEDS_TRANSACTION_TYPES[
                        transactionDetails.type as keyof typeof THREEDS_TRANSACTION_TYPES
                      ]?.label || transactionDetails.type}
                    </span>
                  </p>
                </div>
                <div>
                  <label className='block text-sm font-medium text-t-secondary mb-1'>
                    Transaction Time
                  </label>
                  <p className='text-sm bg-b-surface2 p-2 rounded'>
                    {formatDate(String(transactionDetails?.transactionTime))}
                  </p>
                </div>
              </div>

              <div>
                <label className='block text-sm font-medium text-t-secondary mb-1'>
                  One Time Password (OTP)
                </label>
                <div className='bg-b-surface2 p-3 rounded max-h-32 overflow-y-auto'>
                  <p className='text-sm font-mono break-all text-t-primary'>
                    {transactionDetails.values || 'No values data'}
                  </p>
                </div>
              </div>

              <div>
                <label className='block text-sm font-medium text-t-secondary mb-1'>
                  Description
                </label>
                <p className='text-sm bg-b-surface2 p-2 rounded text-t-primary'>
                  {transactionDetails.description || 'No description'}
                </p>
              </div>
            </div>
          ) : (
            <div className='text-center py-8'>
              <p className='text-t-secondary'>
                No details available for this transaction.
              </p>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

export default ThreeDSTransactions;
